import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');

  const categoryData = categories.find((c) => c.slug === slug) || categories[0];
  const currentSlug = slug || 'dianying';

  const filteredMovies = useMemo(() => {
    let list = movies.filter((m) => m.category === currentSlug);
    if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'year') {
      list = [...list].sort((a, b) => b.year - a.year);
    }
    return list;
  }, [currentSlug, sortBy]);

  return (
    <main>
      <div className="w-full px-4 md:px-6 pt-24 pb-8">
        <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
          {categoryData.name}
        </h1>
        <p className="text-foreground-500 text-sm">
          共 {filteredMovies.length} 部作品
        </p>
      </div>

      <div className="w-full px-4 md:px-6 pb-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <aside className="lg:w-48 shrink-0">
            <div className="flex lg:flex-col gap-1.5 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={cat.icon}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              <button
                onClick={() => setSortBy('default')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === 'default'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                默认
              </button>
              <button
                onClick={() => setSortBy('rating')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === 'rating'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                评分最高
              </button>
              <button
                onClick={() => setSortBy('year')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === 'year'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                最新发布
              </button>
            </div>

            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard
                    key={movie.id}
                    id={movie.id}
                    title={movie.title}
                    year={movie.year}
                    rating={movie.rating}
                    genre={movie.genre}
                    posterUrl={movie.posterUrl}
                    isNew={movie.isNew}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-foreground-400">
                <div className="w-16 h-16 flex items-center justify-center mb-4">
                  <i className="ri-movie-2-line text-4xl"></i>
                </div>
                <p className="text-sm">该分类暂无内容</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${categoryData.name} - 西瓜影视`,
            description: `西瓜影视${categoryData.name}频道，提供海量高清${categoryData.name}在线观看，蓝光超清画质，流畅不缓冲`,
            url: `https://www.xiguays.com/category/${currentSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '西瓜影视',
              url: 'https://www.xiguays.com/',
            },
          }),
        }}
      />
    </main>
  );
}